import { Notifications } from "@mui/icons-material";
import { Badge, CircularProgress, IconButton, Menu, Pagination, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import useNotificationGetAll from "../api/useNotificationGetAll";
import useNotificationUpdateIsRead from "../api/useNotificationUpdateIsRead";
import NotificationItem from "./NotificationItem";

export default function Notification() {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = anchorEl !== null;
    const [page, setPage] = useState(1);
    const { t } = useTranslation("app");
    const pageSize = 10;
    const { updateIsRead } = useNotificationUpdateIsRead();
    const {
        data: notifications,
        totalRows,
        loading,
        unreadNotificationsCount,
    } = useNotificationGetAll({
        page: page - 1,
        pageSize,
        sortOrder: "desc",
    });

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setPage(1);
        if (!notifications) return;
        updateIsRead(notifications.filter(e => !e.isRead).map(e => e.id));
    };

    const handlePageChange = async (nextPage: number) => {
        if (!notifications) return;
        updateIsRead(notifications.filter(e => !e.isRead).map(e => e.id));
        setPage(nextPage);
    };

    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down("sm"));

    const bellIcon = unreadNotificationsCount ? (
        <Badge color="primary" variant="dot">
            <Notifications />
        </Badge>
    ) : (
        <Notifications />
    );

    return (
        <>
            <IconButton onClick={handleClick} aria-label={t("notification.notifications")}>
                {bellIcon}
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                sx={{
                    ".MuiMenu-list": {
                        paddingTop: 0,
                        paddingBottom: 1,
                        width: mobile ? "380px!important" : "500px!important",
                    },
                }}
            >
                {loading ? (
                    <Stack flexDirection="row" justifyContent="center" padding="32px 0">
                        <CircularProgress />
                    </Stack>
                ) : notifications?.length ? (
                    <Stack gap={1} padding={1}>
                        {notifications.map(notification => (
                            <NotificationItem key={notification.id} notification={notification} />
                        ))}
                        <Pagination
                            page={page}
                            count={Math.ceil(totalRows / pageSize)}
                            onChange={(_e, val) => handlePageChange(val)}
                            shape="rounded"
                            size="small"
                            sx={{
                                marginTop: "8px",
                                "& .MuiPagination-ul": {
                                    justifyContent: "end",
                                },
                            }}
                        />
                    </Stack>
                ) : (
                    <Typography variant="body2" padding="16px" textAlign="center">
                        {t("notification.noNotifications")}
                    </Typography>
                )}
            </Menu>
        </>
    );
}
